<template>
  <v-dialog v-model="dialogOpen" max-width="720" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center ga-2">
        <v-icon size="20" color="primary">mdi-email-fast-outline</v-icon>
        <span class="text-subtitle-1">
          {{ $t("ncrm.integrations.mailjet.sendEmail.title") }}
        </span>
      </v-card-title>
      <v-card-subtitle v-if="contactEmail" class="pb-2">
        {{ $t("ncrm.integrations.mailjet.sendEmail.to") }}: <strong>{{ contactEmail }}</strong>
      </v-card-subtitle>
      <v-divider />

      <v-card-text class="pa-4" style="max-height: 580px;">
        <!-- From info (read-only) -->
        <div v-if="senderEmail" class="text-caption text-medium-emphasis mb-3">
          {{ $t("ncrm.integrations.mailjet.sendEmail.from") }}:
          <strong>{{ senderName ? `${senderName} <${senderEmail}>` : senderEmail }}</strong>
        </div>

        <!-- Template dropdown -->
        <v-select
          v-model="selectedTemplateId"
          :items="templates"
          :loading="templatesLoading"
          :label="$t('ncrm.integrations.mailjet.sendEmail.template')"
          item-title="name"
          item-value="id"
          :no-data-text="$t('ncrm.integrations.mailjet.sendEmail.emptyTemplates')"
          :rules="[(v) => Boolean(v) || $t('ncrm.integrations.mailjet.sendEmail.errors.templateRequired')]"
          hide-details="auto"
        />
        <v-alert v-if="selectedTemplate" :type="selectedTemplate.privacy_purpose_id ? 'info' : 'warning'"
          density="compact" class="mt-3">
          <strong>{{ $t("ncrm.integrations.mailjet.sendEmail.dataPurpose") }}:</strong>
          {{ selectedTemplate.privacy_purpose_name || $t("ncrm.integrations.mailjet.sendEmail.noPurpose") }}
        </v-alert>

        <!-- Preview area -->
        <div class="mt-4">
          <div class="text-caption text-medium-emphasis mb-1">
            {{ $t("ncrm.integrations.mailjet.sendEmail.preview") }}
          </div>
          <v-skeleton-loader v-if="previewLoading" type="paragraph@3" :loading="true" />
          <v-alert v-else-if="previewError" type="warning" density="compact">
            {{ previewError }}
          </v-alert>
          <div v-else-if="preview" class="ncrm-mailjet-send__preview">
            <div class="ncrm-mailjet-send__preview-meta">
              <span class="text-caption text-medium-emphasis">
                {{ $t("ncrm.integrations.mailjet.sendEmail.subject") }}:
              </span>
              <strong>{{ preview.subject || "—" }}</strong>
            </div>
            <iframe
              v-if="preview.html_part"
              ref="previewFrame"
              class="ncrm-mailjet-send__iframe"
              :sandbox="iframeSandbox"
              :srcdoc="preview.html_part"
            />
            <pre v-else-if="preview.text_part" class="ncrm-mailjet-send__textpart">{{ preview.text_part }}</pre>
            <div v-else class="text-body-2 text-medium-emphasis">
              {{ $t("ncrm.integrations.mailjet.sendEmail.previewEmpty") }}
            </div>
          </div>
          <div v-else class="text-body-2 text-medium-emphasis">
            {{ $t("ncrm.integrations.mailjet.sendEmail.pickTemplateFirst") }}
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <span v-if="!senderEmail" class="text-caption text-error">
          {{ $t("ncrm.integrations.mailjet.sendEmail.errors.noSender") }}
        </span>
        <v-spacer />
        <v-btn variant="text" :disabled="sending" @click="cancel">
          {{ $t("ncrm.integrations.mailjet.sendEmail.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="sending"
          :disabled="!canSend"
          @click="send"
        >
          <v-icon start size="16">mdi-send-outline</v-icon>
          {{ $t("ncrm.integrations.mailjet.sendEmail.send") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";

// Strict sandbox — block scripts, top-level navigation, popups; we only
// need to render the static HTML the admin already approved in Mailjet.
// allow-same-origin is intentionally OMITTED so that even if a template
// contains a script tag, it cannot access this page's DOM/storage.
const IFRAME_SANDBOX = "";

export default {
  name: "NcrmMailjetSendEmailDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    accountId: { type: [Number, String], required: true },
    contactId: { type: [Number, String], required: true },
    contactEmail: { type: String, default: "" },
    senderEmail: { type: String, default: "" },
    senderName: { type: String, default: "" },
  },
  emits: ["update:modelValue", "sent", "notify"],
  data() {
    return {
      templates: [],
      templatesLoading: false,
      selectedTemplateId: null,
      preview: null,
      previewLoading: false,
      previewError: "",
      sending: false,
      iframeSandbox: IFRAME_SANDBOX,
    };
  },
  computed: {
    dialogOpen: {
      get() { return this.modelValue; },
      set(value) { this.$emit("update:modelValue", value); },
    },
    canSend() {
      return Boolean(
        this.senderEmail &&
        this.selectedTemplateId &&
        !this.sending &&
        !this.previewLoading
      );
    },
    selectedTemplate() {
      return this.templates.find(({ id }) => id === this.selectedTemplateId) || null;
    },
  },
  watch: {
    // Immediate so the API call fires when the parent first mounts the dialog
    // with modelValue=true (same gotcha as the manage-lists dialog).
    modelValue: {
      handler(open) {
        if (open) this.openDialog();
      },
      immediate: true,
    },
    selectedTemplateId(id) {
      if (id) this.fetchPreview(id);
      else this.preview = null;
    },
  },
  methods: {
    async openDialog() {
      this.selectedTemplateId = null;
      this.preview = null;
      this.previewError = "";
      this.templates = [];
      this.templatesLoading = true;
      try {
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/templates/visible`
        );
        this.templates = Array.isArray(data?.templates) ? data.templates : [];
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message:
            error?.response?.data?.error ||
            error?.message ||
            this.$t("ncrm.integrations.mailjet.sendEmail.errors.loadTemplates"),
        });
        this.dialogOpen = false;
      } finally {
        this.templatesLoading = false;
      }
    },
    async fetchPreview(templateId) {
      this.previewLoading = true;
      this.previewError = "";
      this.preview = null;
      try {
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/templates/${templateId}/preview`
        );
        this.preview = data;
      } catch (error) {
        this.previewError =
          error?.response?.data?.error ||
          error?.message ||
          this.$t("ncrm.integrations.mailjet.sendEmail.errors.loadPreview");
      } finally {
        this.previewLoading = false;
      }
    },
    cancel() {
      this.dialogOpen = false;
    },
    async send() {
      if (!this.canSend) return;
      this.sending = true;
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/contacts/${this.contactId}/send-template`,
          { templateId: this.selectedTemplateId }
        );
        this.$emit("notify", {
          color: "success",
          message: this.$t("ncrm.integrations.mailjet.sendEmail.alerts.success"),
        });
        this.$emit("sent", data);
        this.dialogOpen = false;
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message:
            error?.response?.data?.error ||
            error?.message ||
            this.$t("ncrm.integrations.mailjet.sendEmail.errors.send"),
        });
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-mailjet-send__preview {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 12px;
  background: var(--app-surface-2, rgba(0, 0, 0, 0.02));
}
.ncrm-mailjet-send__preview-meta {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.ncrm-mailjet-send__iframe {
  width: 100%;
  height: 280px;
  border: 0;
  background: #fff;
  border-radius: 4px;
}
.ncrm-mailjet-send__textpart {
  white-space: pre-wrap;
  font-family: var(--app-font-family-mono);
  font-size: 12px;
  background: #fff;
  padding: 8px;
  border-radius: 4px;
  margin: 0;
  max-height: 280px;
  overflow: auto;
}
</style>
